import {
  FieldClass,
  ISchemaDataModelFieldsFragment,
  ITableColumnFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { useCallback, useMemo } from 'react';
import { dataModelerSelectCurrentDraftEdits } from '../../../../redux/selectors/dataModeler';
import {
  getModelField,
  temporalFieldRefsToTableColumnsDraftData,
} from '../utils';
import { ISubjectListItem } from './types';
import {
  dataModelerAddDraftInstallTemporals,
  dataModelerExtendFactSubject,
  dataModelerRemoveDraftInstallTemporals,
  dataModelerRemoveDraftSubject,
  dataModelerSetDraftSubject,
  dataModelerSetDraftSubjectName,
  dataModelerSetEventColumn,
  dataModelerSetEventTemporalColumn,
  dataModelerSetInstallTemporalColumn,
} from '../../../../redux/slices/dataModeler';
import { useSubjectNameValidatorFunc } from '../validation';

export const useFactRequiredFieldsModel = ({
  schemaObj,
  isEditMode,
  tableKey,
  availableColumnsFromServer,
}: {
  schemaObj: ISchemaDataModelFieldsFragment;
  isEditMode: boolean;
  tableKey: string;
  availableColumnsFromServer: ITableColumnFieldsFragment[];
}) => {
  const dispatch = useAppDispatch();
  const isExistingTable = useMemo(() => {
    return schemaObj.tables.some((table) => table.key === tableKey);
  }, [schemaObj.tables, tableKey]);
  const canEdit = isEditMode && !isExistingTable;
  const draftEdits = useAppSelector(dataModelerSelectCurrentDraftEdits);

  // event
  const eventColumn: ITableColumnFieldsFragment | undefined = useMemo(() => {
    const existingEventColumnName = schemaObj.fact.fields
      .find((field) => field.fieldClass === FieldClass.Event)
      ?.tableFields.find(
        (tableField) => tableField.tableKey === tableKey,
      )?.column;
    return existingEventColumnName
      ? availableColumnsFromServer.find(
          (cdwColumn) => cdwColumn.column === existingEventColumnName,
        )
      : draftEdits?.draftFieldsData.find(
          (draftField) =>
            draftField.tableKey === tableKey &&
            (draftField.extendInput?.typeInput?.fieldClass ===
              FieldClass.Event ||
              draftField.addInput?.typeInput?.fieldClass === FieldClass.Event),
        )?.column;
  }, [
    availableColumnsFromServer,
    draftEdits?.draftFieldsData,
    schemaObj.fact.fields,
    tableKey,
  ]);

  const handleSetEventColumn = useCallback(
    (tableColumn: ITableColumnFieldsFragment) => {
      dispatch(
        dataModelerSetEventColumn({
          tableColumn,
          tableKey,
        }),
      );
    },
    [dispatch, tableKey],
  );

  // event ts/date
  const eventTemporalsData = useMemo(() => {
    return (
      draftEdits?.addedFactsData?.find(
        (addedFact) => addedFact.tableKey === tableKey,
      )?.temporals ??
      temporalFieldRefsToTableColumnsDraftData(
        schemaObj.eventTemporals,
        tableKey,
      )
    );
  }, [draftEdits?.addedFactsData, schemaObj.eventTemporals, tableKey]);

  const handleSetTimestamp = useCallback(
    (tableColumn: ITableColumnFieldsFragment) => {
      dispatch(
        dataModelerSetEventTemporalColumn({
          tableColumn,
          tableKey,
          temporalField: 'ts',
        }),
      );
    },
    [dispatch, tableKey],
  );

  const handleSetDate = useCallback(
    (tableColumn: ITableColumnFieldsFragment) => {
      dispatch(
        dataModelerSetEventTemporalColumn({
          tableColumn,
          tableKey,
          temporalField: 'date',
        }),
      );
    },
    [dispatch, tableKey],
  );

  // subjects
  const factExistingSubjectsList: ISubjectListItem[] = useMemo(
    () =>
      schemaObj.subjects
        .filter((subj) => subj.viewKey === schemaObj.fact.key)
        .map((subj) => {
          const fieldMeta = getModelField(schemaObj, subj.fieldKey);
          const tableField = fieldMeta?.tableFields?.find(
            (tableField) => tableField.tableKey === tableKey,
          );
          const extendColumnData = draftEdits?.draftFieldsData.find(
            (draft) => draft.extendInput?.fieldKey === fieldMeta?.key,
          );
          return {
            subjectName: subj.name,
            subjectKey: subj.subjectKey,
            fieldKey: subj.fieldKey,
            columnName:
              tableField?.column ?? extendColumnData?.column?.column ?? null,
            tableColumn: extendColumnData?.column ?? null,
            // pre-existing in the schema model mapping can not be edited,
            // we also don't get a tableColumn in that case
            canExtend: !tableField,
            isNew: false,
          };
        }),
    [draftEdits?.draftFieldsData, schemaObj, tableKey],
  );

  const factNewSubjectsList = useMemo(
    () =>
      draftEdits?.addedSubjects
        .filter((addedSubj) => addedSubj.viewKey === schemaObj.fact.key)
        .map((subj) => {
          const addedTableField =
            draftEdits?.draftFieldsData.find(
              (draft) => draft.addInput?.key === subj.input.fieldKey,
            ) ?? null;
          const extendedTableColumns =
            draftEdits?.draftFieldsData.filter(
              (draft) => draft.extendInput?.fieldKey === subj.input.fieldKey,
            ) ?? [];
          const draftField = [
            ...(addedTableField ? [addedTableField] : []),
            ...extendedTableColumns,
          ].find((tc) => tc.tableKey === tableKey);
          return {
            subjectName: subj.input.subjectName,
            subjectKey: subj.subjectKey,
            fieldKey: subj.input.fieldKey,
            columnName: draftField?.column?.column ?? null,
            tableColumn: draftField?.column ?? null,
            canExtend: !!addedTableField && !draftField?.addInput,
            isNew: true,
          };
        }) ?? [],
    [
      draftEdits?.addedSubjects,
      draftEdits?.draftFieldsData,
      schemaObj.fact.key,
      tableKey,
    ],
  );

  const subjectsList: ISubjectListItem[] = useMemo(
    () => [...factExistingSubjectsList, ...factNewSubjectsList],
    [factExistingSubjectsList, factNewSubjectsList],
  );

  const subjectRequired =
    schemaObj.subjects.length === 0 &&
    (draftEdits?.addedSubjects?.length ?? 0) === 0;

  const subjectNameValidatorFunc = useSubjectNameValidatorFunc();

  const suggestedNextSubjectName = useMemo(() => {
    const func = subjectNameValidatorFunc();
    if (func.safeParse({ value: 'User' }).success) {
      return 'User';
    }
    if (func.safeParse({ value: 'Session' }).success) {
      return 'Session';
    }
    if (func.safeParse({ value: 'Device' }).success) {
      return 'Device';
    }
    return 'New Subject Name';
  }, [subjectNameValidatorFunc]);

  const addNewSubject = useCallback(
    (subjectName: string, tableColumn: ITableColumnFieldsFragment) => {
      dispatch(
        dataModelerSetDraftSubject({
          viewKey: schemaObj.fact.key,
          tableKey,
          subjectName,
          subjectKey: null,
          tableColumn,
        }),
      );
    },
    [dispatch, schemaObj, tableKey],
  );

  const removeNewSubject = useCallback(
    (subjectKey: string) => {
      dispatch(
        dataModelerRemoveDraftSubject({
          subjectKey,
        }),
      );
    },
    [dispatch],
  );

  const updateSubjectColumn = useCallback(
    (
      subjectKey: string,
      tableKey: string,
      tableColumn: ITableColumnFieldsFragment,
    ) => {
      const subjectItem = subjectsList.find(
        (subj) => subj.subjectKey === subjectKey,
      );
      if (!subjectItem) {
        return;
      }
      if (subjectItem.canExtend) {
        dispatch(
          dataModelerExtendFactSubject({
            tableKey,
            subjectKey,
            tableColumn,
          }),
        );
      } else {
        dispatch(
          dataModelerSetDraftSubject({
            subjectName: subjectItem.subjectName,
            subjectKey,
            viewKey: schemaObj.fact.key,
            tableKey,
            tableColumn,
          }),
        );
      }
    },
    [dispatch, schemaObj.fact.key, subjectsList],
  );

  const updateSubjectName = useCallback(
    (subjectKey: string, subjectName: string) => {
      dispatch(dataModelerSetDraftSubjectName({ subjectKey, subjectName }));
    },
    [dispatch],
  );

  // install date
  const installTemporalsData = useMemo(() => {
    if (
      schemaObj.installTemporals &&
      schemaObj.installTemporals.viewKey !== schemaObj.fact.key
    ) {
      return undefined;
    }
    return (
      draftEdits?.editTablesData?.find((draft) => draft.tableKey === tableKey)
        ?.installTemporals ??
      draftEdits?.addedFactsData?.find(
        (addedFact) => addedFact.tableKey === tableKey,
      )?.installTemporals ??
      (schemaObj.installTemporals
        ? temporalFieldRefsToTableColumnsDraftData(
            schemaObj.installTemporals,
            tableKey,
          )
        : undefined)
    );
  }, [
    draftEdits?.editTablesData,
    draftEdits?.addedFactsData,
    schemaObj.fact.key,
    schemaObj.installTemporals,
    tableKey,
  ]);

  const installTemporalsSetDate = useCallback(
    (tableColumn: ITableColumnFieldsFragment) => {
      dispatch(
        dataModelerSetInstallTemporalColumn({
          viewKey: schemaObj.fact.key,
          tableColumn,
          tableKey,
          temporalField: 'date',
        }),
      );
    },
    [dispatch, schemaObj.fact.key, tableKey],
  );

  const installTemporalsInit = useCallback(() => {
    dispatch(
      dataModelerAddDraftInstallTemporals({
        viewKey: schemaObj.fact.key,
      }),
    );
  }, [dispatch, schemaObj.fact.key]);

  const installTemporalsRemove = useCallback(() => {
    dispatch(
      dataModelerRemoveDraftInstallTemporals({
        viewKey: schemaObj.fact.key,
        tableKey,
      }),
    );
  }, [dispatch, schemaObj.fact.key, tableKey]);

  const installTemporalsCanInit = useMemo(
    () =>
      !installTemporalsData &&
      !schemaObj.installTemporals &&
      !draftEdits?.editTablesData?.some((draft) => !!draft.installTemporals) &&
      !draftEdits?.addedFactsData?.some((draft) => !!draft.installTemporals) &&
      !draftEdits?.addedDimensionsData?.some(
        (dimData) => !!dimData.installTemporals,
      ),
    [
      draftEdits?.addedDimensionsData,
      draftEdits?.addedFactsData,
      draftEdits?.editTablesData,
      installTemporalsData,
      schemaObj.installTemporals,
    ],
  );

  return {
    canEdit,
    // event
    eventColumn,
    handleSetEventColumn,
    // subject
    subjectRequired,
    subjectsList,
    addNewSubject,
    removeNewSubject,
    updateSubjectColumn,
    // isValidNewSubjectName,
    subjectNameValidatorFunc,
    suggestedNextSubjectName,
    updateSubjectName,
    // event date/ts
    eventTemporalsData,
    handleSetTimestamp,
    handleSetDate,
    // install date (optional)
    installTemporalsData,
    installTemporalsCanInit,
    installTemporalsInit,
    installTemporalsRemove,
    installTemporalsSetDate,
  };
};
